import { ShieldCheck } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/Eyebrow";

/**
 * Phase 3 — shared tool page wrapper.
 * Renders a consistent dark-academia hero band + the standing disclaimer strip,
 * then yields the rest of the page body to its children.
 */
export default function ToolPage({ numeral, name, blurb, children, seoTitle, seoDescription, seoCanonical, seoKeywords }) {
  return (
    <Layout>
      {seoTitle && (
        <SEO
          title={seoTitle}
          description={seoDescription}
          canonical={seoCanonical}
          keywords={seoKeywords}
        />
      )}
      <section className="border-b border-stone/30 bg-bone" data-testid={`tool-page-${(name || "").toLowerCase().replace(/\s+/g, "-")}`}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 lg:pt-24 pb-12">
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-brass text-5xl leading-none">{numeral}</span>
            <Eyebrow>Field Instrument · Phase 3</Eyebrow>
          </div>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[4.5rem] text-ink leading-[1.04] tracking-tightest mt-5">
            {name}
          </h1>
          {blurb && <p className="mt-6 text-ink-soft text-base sm:text-lg max-w-2xl leading-relaxed">{blurb}</p>}
        </div>
      </section>

      <div className="bg-bone-dim border-b border-stone/30">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3 flex items-start gap-3" data-testid="tool-disclaimer-strip">
          <ShieldCheck className="h-4 w-4 text-brass mt-0.5 flex-shrink-0" />
          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft">
            [Disclaimer — informational only, not an admissions decision]
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 lg:py-20">{children}</div>
    </Layout>
  );
}

export const inputCls = "rounded-none border-stone/50 bg-bone focus-visible:border-ivy focus-visible:ring-0";
export const labelCls = "text-[11px] uppercase tracking-[0.22em] text-stone";
export const primaryBtn = "bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors disabled:opacity-60";
export const secondaryBtn = "border border-stone/50 hover:border-ink text-ink-soft uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 transition-colors";
